import { EpisodeStatusTag } from '@/components/episodes/EpisodeStatusTag';
import { Button } from '@/components/ui/Button';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { AxleSuspense } from '@/fetching/suspense';
import { patientEpisodeViewEpisodeAtom } from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/PatientEpisodeView.atoms';
import { getEpisodeLabel } from '@/utils/episodeLabel';
import {
    CalendarMonth,
    ExpandLess,
    ExpandMore,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { default as Grid, default as Grid2 } from '@mui/material/Unstable_Grid2';
import { EpisodeStatus } from 'gql-types';
import { useAtomValue } from 'jotai';
import moment from 'moment';
import { FC, useCallback, useRef, useState } from 'react';

import { PatientEpisodeCalendar } from './PatientEpisodeCalendar/PatientEpisodeCalendar';
import { PatientEpisodeSelector } from './PatientEpisodeSelector/PatientEpisodeSelector';

const EPISODE_DATE_FORMAT = 'ddd, MMM D, YYYY';

export const formatEpisodeWindow = (startDate: Date, endDate: Date, format?: string): string =>
    moment(startDate).format(format ?? EPISODE_DATE_FORMAT) +
    ' – ' +
    moment(endDate).format(format ?? EPISODE_DATE_FORMAT);

const EpisodeDateRangeText: FC = () => {
    const episode = useAtomValue(patientEpisodeViewEpisodeAtom);

    if (!episode) {
        return null;
    }

    return (
        <Box position={'relative'} width={'100%'}>
            <Box width={'100%'}>
                <Grid2 container alignItems={'center'}>
                    <Grid2
                        xs={3}
                        display={
                            episode.status === EpisodeStatus.ACTIVE ||
                            episode.status === EpisodeStatus.RECERTIFIED
                                ? 'none'
                                : 'flex'
                        }
                        sx={{ opacity: 0 }}
                    >
                        <EpisodeStatusTag status={episode.status} />
                    </Grid2>
                    <Grid2 xs={6} flexGrow={1}>
                        <Typography variant='h2' fontSize={24} lineHeight={1.3333} fontWeight={500}>
                            {getEpisodeLabel(episode.typeLabel, episode.numberInSequence)}
                        </Typography>
                    </Grid2>
                    <Grid2
                        xs={3}
                        display={
                            episode.status === EpisodeStatus.ACTIVE ||
                            episode.status === EpisodeStatus.RECERTIFIED
                                ? 'none'
                                : 'flex'
                        }
                    >
                        <EpisodeStatusTag status={episode.status} />
                    </Grid2>
                </Grid2>
                <Typography component='div'>
                    {episode.startDate && episode.endDate
                        ? formatEpisodeWindow(episode.startDate, episode.endDate)
                        : ''}
                </Typography>
            </Box>
        </Box>
    );
};

const EpisodeTitle: FC = () => {
    const episode = useAtomValue(patientEpisodeViewEpisodeAtom);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleClick = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const shouldDisplaySelector = episode && episode.patient.episodes.totalCount > 1;

    if (!shouldDisplaySelector) {
        return <EpisodeDateRangeText />;
    }

    return (
        <>
            <Button
                ref={buttonRef}
                onClick={handleClick}
                aria-label='Select patient episode'
                endIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
                sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                }}
            >
                <EpisodeDateRangeText />
            </Button>
            <AxleSuspense customFallback={null}>
                <PatientEpisodeSelector
                    patientId={episode.patient.id || ''}
                    open={isOpen}
                    anchorEl={buttonRef.current}
                    handleClose={handleClose}
                />
            </AxleSuspense>
        </>
    );
};

const PendingAdmissionTitle: FC = () => {
    return (
        <>
            <Typography variant='h2' fontSize={24} lineHeight={1.3333} fontWeight={500}>
                Patient Pending Admission
            </Typography>
            <Typography>No dates available</Typography>
        </>
    );
};

// This header displays either the current page's selected episode or
// the patient's pending admission status if there is no episode yet.
export const PatientEpisodeViewHeader: FC = () => {
    const episode = useAtomValue(patientEpisodeViewEpisodeAtom);
    const patientId = episode?.patient.id ?? null;
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const toggleMode = useCallback(() => {
        setIsCalendarOpen((prev) => !prev);
    }, []);

    return (
        <Grid container>
            <Grid xs={6} xsOffset={3}>
                <VerticalStack textAlign='center'>
                    {episode ? <EpisodeTitle /> : <PendingAdmissionTitle />}
                </VerticalStack>
            </Grid>
            <Grid xs={3} display='flex' justifyContent='flex-end'>
                <Button
                    startIcon={<CalendarMonth />}
                    size='small'
                    sx={{
                        textTransform: 'none',
                        height: '40px',
                        borderRadius: '4px',
                    }}
                    onClick={toggleMode}
                    endIcon={isCalendarOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    variant='outlined'
                    color='secondary'
                    disabled={!episode}
                >
                    {isCalendarOpen ? 'Close Calendar' : 'View Calendar'}
                </Button>
            </Grid>
            <Grid xs={12}>
                <Collapse
                    in={isCalendarOpen && !!episode && !!patientId}
                    mountOnEnter
                    unmountOnExit
                >
                    {episode && !!patientId && (
                        <PatientEpisodeCalendar episode={episode} patientId={patientId} />
                    )}
                </Collapse>
            </Grid>
        </Grid>
    );
};
